import React, { useContext, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link as RouterLink, useLocation, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Badge,
  Box,
  Divider,
  Drawer,
  Hidden,
  List,
  Typography,
  Tooltip,
} from '@mui/material';
import PersonOutlineOutlined from '@mui/icons-material/PersonOutlineOutlined';
import BusinessCenterOutlined from '@mui/icons-material/BusinessCenterOutlined';
import GroupsOutlined from '@mui/icons-material/GroupsOutlined';
import AccountBalanceWalletOutlined from '@mui/icons-material/AccountBalanceWalletOutlined';
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined';
import InsightsOutlined from '@mui/icons-material/InsightsOutlined';
import SettingsOutlined from '@mui/icons-material/SettingsOutlined';
import ReceiptLongOutlined from '@mui/icons-material/ReceiptLongOutlined';
import AccountBalanceOutlined from '@mui/icons-material/AccountBalanceOutlined';
import CampaignOutlined from '@mui/icons-material/CampaignOutlined';
import AnnouncementOutlined from '@mui/icons-material/AnnouncementOutlined';
import NavItem from './NavItem';
import { PassportAuth } from '../../contexts/AuthContext';
import { getStorage } from '../../utils/functions/localstorage';
import { accessRightSubMenu } from '../../utils/functions/accessRight';
import { getOrg } from '../../utils/orgConfig';
import EmployeeAvatar from '../EmployeeAvatar';
import OrgBrand from '../OrgBrand';
import {
  SIDEBAR_NAV_ITEMS,
  SIDEBAR_MODULE_TITLE,
} from '../../constants/sidebarNavConfig';
import * as actions from '../../redux/actions';
import api from '../../utils/functions/api';
import { isBudgetApprover } from '../../utils/functions/approverAccess';
import { useSocket } from '../../contexts/SocketContext';
import { useAnnouncements } from '../../contexts/AnnouncementContext';
import { getApproverSlots } from '../../utils/functions/approverAccess';

/** ไอคอนหนึ่งต่อโมดูล — แถบซ้ายหลักเท่านั้น (อ่านง่าย) */
const SIDEBAR_MODULE_ICONS = {
  PROFILE: PersonOutlineOutlined,
  PROJECT: BusinessCenterOutlined,
  HUMEN: GroupsOutlined,
  FINANCE: AccountBalanceWalletOutlined,
  SALE: ReceiptLongOutlined,
  CUSTOMER: StorefrontOutlined,
  DIRECTOR: InsightsOutlined,
  MANAGEMENT: SettingsOutlined,
  ACCOUNTING: AccountBalanceOutlined,
  MARKETING: CampaignOutlined,
};

const SIDEBAR_MODULE_SHORT_TITLE = {
  PROFILE: 'โปรไฟล์',
  PROJECT: 'โปรเจกต์',
  HUMEN: 'บุคคล',
  FINANCE: 'การเงิน',
  SALE: 'ขาย',
  CUSTOMER: 'ลูกค้า',
  DIRECTOR: 'ผู้บริหาร',
  MANAGEMENT: 'ตั้งค่า',
  ACCOUNTING: 'บัญชี',
  MARKETING: 'การตลาด',
};

// ซ่อนเมนู "อนุมัติการจ่ายเงิน" จากคนที่ไม่ได้ถูกตั้งเป็นผู้อนุมัติไว้ที่ /management/approvers
// (Information.approver_settings key: 'payment', ONE_STEP เท่านั้น — ใครก็ได้ใน 3 คนนี้ถือว่ามีสิทธิ์)
const isPaymentApprover = (me, info) => {
  const myId = me?.userData?._id;
  if (!myId) return false;
  const cfg = (info?.approver_settings || []).find((c) => c.key === 'payment');
  if (!cfg) return false;
  const ids = getApproverSlots(cfg)
    .map((v) => v?._id || v)
    .filter(Boolean)
    .map((v) => v.toString());
  return ids.includes(myId.toString());
};

// ซ่อนเมนู "ตรวจสอบรายการเบิกเงิน" จากคนที่ไม่ได้ถูกตั้งเป็นผู้ตรวจสอบไว้ที่ /management/approvers —
// mirror isPaymentApprover ข้างบนเป๊ะ แต่เช็ค key: 'expense_reviewer' แทน
const isExpenseReviewer = (me, info) => {
  const myId = me?.userData?._id;
  if (!myId) return false;
  const cfg = (info?.approver_settings || []).find((c) => c.key === 'expense_reviewer');
  if (!cfg) return false;
  const ids = getApproverSlots(cfg)
    .map((v) => v?._id || v)
    .filter(Boolean)
    .map((v) => v.toString());
  return ids.includes(myId.toString());
};

const PRIMARY_RAIL_WIDTH = 80;
const SECONDARY_RAIL_WIDTH = 240;

// สีแบรนด์ของ sidebar อ้าง CSS variable เดียวกับ Tailwind (ตั้งจาก org/theme.json ตอนเปิดเว็บ,
// ดู utils/orgConfig.js) — ค่าเริ่มต้นได้สีเดิมเป๊ะ: พื้นแถบแรก = primary 900, ปุ่มที่เลือก = primary 500
const BRAND_RAIL_BG = 'rgb(var(--color-primary-900))';
const BRAND_ACTIVE = 'rgb(var(--color-primary-500))';
const BRAND_ACTIVE_SHADOW = '0 4px 12px rgb(var(--color-primary-500) / 0.4)';

const items = SIDEBAR_NAV_ITEMS;

const DashboardSidebar = ({ me, onMobileClose, openMobile }) => {
  const orgBrand = getOrg();
  const {
    username = '',
    firstname = '',
    lastname = '',
    department: { name: departmentName = '' } = {},
  } = JSON.parse(getStorage('remember') || '{}');

  const { handleSignout } = useContext(PassportAuth);
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  const [activeModuleName, setActiveModuleName] = React.useState('');
  const [peek, setPeek] = React.useState(false);
  const peekTimeoutRef = React.useRef(null);

  const handlePeekEnter = React.useCallback(() => {
    if (peekTimeoutRef.current) {
      clearTimeout(peekTimeoutRef.current);
      peekTimeoutRef.current = null;
    }
    setPeek(true);
  }, []);

  const handlePeekLeave = React.useCallback(() => {
    peekTimeoutRef.current = setTimeout(() => setPeek(false), 250);
  }, []);

  useEffect(() => () => {
    if (peekTimeoutRef.current) clearTimeout(peekTimeoutRef.current);
  }, []);

  const pendingLeaveCount = useSelector((state) => state.leaveRequest?.pendingCount || 0);
  const pendingOtCount = useSelector((state) => state.otRequest?.pendingCount || 0);
  const pendingFieldAllowanceCount = useSelector((state) => state.fieldAllowanceRequestOrder?.pendingCount || 0);
  const pendingExpensesCount = useSelector((state) => state.expenses?.pendingCount || 0);
  const pendingPaymentApprovalCount = useSelector((state) => state.expenses?.paymentApprovalPendingCount || 0);
  const pendingExpenseReviewCount = useSelector((state) => state.expenses?.reviewPendingCount || 0);
  const pendingTimestampApprovalCount = useSelector((state) => state.timestamp?.pendingApprovalCount || 0);
  const pendingEmployeeCount = useSelector((state) => state.employee?.pendingCount || 0);
  const socket = useSocket();
  const { unreadCount: announcementUnread, openList: openAnnouncementList } = useAnnouncements();
  const openAnnouncements = () => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
    openAnnouncementList();
  };
  const info = useSelector((state) => state.info);
  const hiddenMenus = React.useMemo(
    () => new Set(info?.setting?.hidden_menus || []),
    [info?.setting?.hidden_menus],
  );

  const onSignOut = async () => {
    await handleSignout();
    history.push('/login');
  };

  useEffect(() => {
    if (openMobile && onMobileClose) {
      onMobileClose();
    }
  }, [location.pathname]);

  useEffect(() => {
    dispatch(actions.getInformation());
  }, []);

  const moduleGroups = React.useMemo(() => {
    const groups = {};
    items.forEach((item) => {
      const rowVisible = item.modulePermission
        ? Boolean(me?.userData?.permissions?.[item.modulePermission]) ||
          accessRightSubMenu(me, item.name, item.href, 0)
        : item.sub && item.sub.length > 0
          ? item.sub.some((s) => accessRightSubMenu(me, item.name, s.href, s.level ?? 0) && !hiddenMenus.has(s.href))
          : accessRightSubMenu(me, item.name, item.href, 0) && !hiddenMenus.has(item.href);
      if (!rowVisible) return;
      if (!groups[item.name]) {
        const ModuleIcon =
          SIDEBAR_MODULE_ICONS[item.name] || SettingsOutlined;
        groups[item.name] = {
          name: item.name,
          title: SIDEBAR_MODULE_TITLE[item.name] || item.title,
          moduleIcon: ModuleIcon,
          href: item.href,
          items: [],
        };
      }
      groups[item.name].items.push(item);
    });
    return Object.values(groups);
  }, [me, hiddenMenus]);

  const hasHumenAccess = React.useMemo(
    () => moduleGroups.some((m) => m.name === 'HUMEN'),
    [moduleGroups],
  );

  const hasFinanceAccess = React.useMemo(
    () => moduleGroups.some((m) => m.name === 'FINANCE'),
    [moduleGroups],
  );

  useEffect(() => {
    if (!hasHumenAccess) return;
    dispatch(actions.leaveRequestPendingCount());
    dispatch(actions.otRequestPendingCount());
    dispatch(actions.fieldAllowanceRequestOrderPendingCount());
    dispatch(actions.timestampPendingApprovalCount());
    dispatch(actions.employeePendingCount());
    const timer = setInterval(() => {
      dispatch(actions.leaveRequestPendingCount());
      dispatch(actions.otRequestPendingCount());
      dispatch(actions.fieldAllowanceRequestOrderPendingCount());
      dispatch(actions.timestampPendingApprovalCount());
      dispatch(actions.employeePendingCount());
    }, 120000);
    return () => clearInterval(timer);
  }, [hasHumenAccess]);

  useEffect(() => {
    if (!hasFinanceAccess) return;
    dispatch(actions.expensesPendingCount());
    const timer = setInterval(() => dispatch(actions.expensesPendingCount()), 120000);
    return () => clearInterval(timer);
  }, [hasFinanceAccess]);

  // จำนวนงวด "รายการเบิกประจำ" ที่ถึงช่วงเตือน/เลยกำหนดแล้วยังไม่ได้กดสร้างรายการเบิก — สถานะเปลี่ยนตามวันที่
  // (ไม่มี event ตอนเข้าช่วงเตือน) เลยพึ่ง interval เป็นหลัก + refetch ตอนมีรายการเบิกถูกสร้าง
  const [recurringDueCount, setRecurringDueCount] = React.useState(0);
  useEffect(() => {
    if (!hasFinanceAccess) return undefined;
    const fetchRecurringDueCount = async () => {
      try {
        const { data } = await api.get(`${process.env.REACT_APP_API_URL}/expenses-recurring/due-count`);
        setRecurringDueCount(data?.count || 0);
      } catch (error) {
        console.error(error);
      }
    };
    fetchRecurringDueCount();
    const timer = setInterval(fetchRecurringDueCount, 120000);
    socket?.on('expenses:created', fetchRecurringDueCount);
    return () => {
      clearInterval(timer);
      socket?.off('expenses:created', fetchRecurringDueCount);
    };
  }, [hasFinanceAccess, socket]);

  const canApprovePayment = isPaymentApprover(me, info);
  useEffect(() => {
    if (!canApprovePayment) return;
    dispatch(actions.expensesPaymentApprovalPendingCount());
    const timer = setInterval(() => dispatch(actions.expensesPaymentApprovalPendingCount()), 120000);
    return () => clearInterval(timer);
  }, [canApprovePayment]);

  // จำนวน budget ที่รออนุมัติ (เฉพาะผู้อนุมัติ budget) — โชว์ badge ที่เมนู "อนุมัติ budget" + ไอคอนโมดูลโครงการ
  const canApproveBudget = isBudgetApprover(me, info);
  const [pendingBudgetCount, setPendingBudgetCount] = React.useState(0);
  useEffect(() => {
    if (!canApproveBudget) return undefined;
    const fetchBudgetPending = async () => {
      try {
        const { data } = await api.get(`${process.env.REACT_APP_API_URL}/budget/pending-approval?status=PENDING`);
        setPendingBudgetCount(data?.rows?.length || 0);
      } catch (error) {
        console.error(error);
      }
    };
    fetchBudgetPending();
    const timer = setInterval(fetchBudgetPending, 120000);
    socket?.on('budget:updated', fetchBudgetPending);
    return () => {
      clearInterval(timer);
      socket?.off('budget:updated', fetchBudgetPending);
    };
  }, [canApproveBudget, socket]);

  const canReviewExpense = isExpenseReviewer(me, info);
  useEffect(() => {
    if (!canReviewExpense) return;
    dispatch(actions.expensesReviewPendingCount());
    const timer = setInterval(() => dispatch(actions.expensesReviewPendingCount()), 120000);
    return () => clearInterval(timer);
  }, [canReviewExpense]);

  useEffect(() => {
    if (!socket) return;
    // เงื่อนไขสิทธิ์ต้องตรงกับ effect ดึงตัวเลขตอนเปิดหน้า/ทุก 2 นาทีด้านบน — socket event broadcast ไปทุกเครื่อง
    // (ไม่มี room) เดิมไม่เช็คสิทธิ์ตรงนี้ เลยยิง /leave-request, /field-allowance-request-order (backend กัน
    // HUMEN/PROFILE) จากเครื่องพนักงานที่ไม่มีสิทธิ์ทุกครั้งที่คนอื่นแก้คำขอลา/เบี้ยเลี้ยง → ขึ้น dialog 403
    const refreshExpenses = () => {
      if (hasFinanceAccess) dispatch(actions.expensesPendingCount());
      if (canApprovePayment) dispatch(actions.expensesPaymentApprovalPendingCount());
      if (canReviewExpense) dispatch(actions.expensesReviewPendingCount());
    };
    const refreshLeave = () => hasHumenAccess && dispatch(actions.leaveRequestPendingCount());
    const refreshOt = () => hasHumenAccess && dispatch(actions.otRequestPendingCount());
    const refreshFieldAllowance = () => hasHumenAccess && dispatch(actions.fieldAllowanceRequestOrderPendingCount());
    const refreshTimestampApproval = () => hasHumenAccess && dispatch(actions.timestampPendingApprovalCount());
    const refreshEmployee = ({ pendingCount }) => dispatch({ type: 'EMPLOYEE_PENDING_COUNT', payload: pendingCount });
    socket.on('expenses:created', refreshExpenses);
    socket.on('expenses:updated', refreshExpenses);
    socket.on('leaveRequest:updated', refreshLeave);
    socket.on('otRequest:updated', refreshOt);
    socket.on('fieldAllowanceRequest:updated', refreshFieldAllowance);
    socket.on('timestamp:created', refreshTimestampApproval);
    socket.on('timestamp:updated', refreshTimestampApproval);
    socket.on('onboarding:submitted', refreshEmployee);
    return () => {
      socket.off('expenses:created', refreshExpenses);
      socket.off('expenses:updated', refreshExpenses);
      socket.off('leaveRequest:updated', refreshLeave);
      socket.off('otRequest:updated', refreshOt);
      socket.off('fieldAllowanceRequest:updated', refreshFieldAllowance);
      socket.off('timestamp:created', refreshTimestampApproval);
      socket.off('timestamp:updated', refreshTimestampApproval);
      socket.off('onboarding:submitted', refreshEmployee);
    };
  }, [socket, canApprovePayment, canReviewExpense, hasHumenAccess, hasFinanceAccess]);

  useEffect(() => {
    const pathMatch = (href) =>
      location.pathname === href || location.pathname.startsWith(href + '/');
    const found = moduleGroups.find((m) =>
      m.items.some(
        (item) =>
          pathMatch(item.href) ||
          (item?.sub && item.sub.some((s) => pathMatch(s.href))),
      ),);
    if (found) {
      setActiveModuleName(found.name);
    } else if (moduleGroups.length > 0 && !activeModuleName) {
      setActiveModuleName(moduleGroups[0].name);
    }
  }, [location.pathname, moduleGroups]);

  const activeGroup = moduleGroups.find((m) => m.name === activeModuleName);

  /* Primary rail — ไอคอนโมดูล (ค้างไว้เสมอ) */
  const primaryRailBox = (
    <Box
      sx={{
        width: PRIMARY_RAIL_WIDTH,
        height: '100%',
        backgroundColor: BRAND_RAIL_BG,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 3,
        zIndex: 10,
        boxShadow: '4px 0 10px rgba(0,0,0,0.1)',
      }}
    >
      {/* โลโก้บริษัทจาก org/ — ไม่มีไฟล์/ชื่อ = ไม่แสดงอะไร (แถบนี้แคบ ชื่ออยู่ใน tooltip) */}
      {(orgBrand.logoUrl || orgBrand.company.name) && (
        <Box sx={{ mb: 2, maxWidth: PRIMARY_RAIL_WIDTH - 16 }}>
          <OrgBrand showName={false} logoHeight={32} logoMaxWidth={PRIMARY_RAIL_WIDTH - 32} logoPlate />
        </Box>
      )}
      <Box sx={{ mb: 4, textAlign: 'center', maxWidth: PRIMARY_RAIL_WIDTH - 8 }}>
        <EmployeeAvatar
          employee={me?.userData}
          size={48}
          sx={{
            cursor: 'pointer',
            border: '2px solid rgba(255,255,255,0.2)',
            transition: 'transform 0.2s',
            bgcolor: 'rgba(255,255,255,0.15)',
            color: '#fff',
            mx: 'auto',
            '&:hover': { transform: 'scale(1.05)', borderColor: '#fff' },
          }}
        />
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 0.5,
            color: '#fff',
            fontSize: '0.62rem',
            fontWeight: 700,
            lineHeight: 1.1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={me?.userData?.firstname || me?.userData?.firstname_th || firstname || username}
        >
          {me?.userData?.firstname || me?.userData?.firstname_th || firstname || username || '-'}
        </Typography>
      </Box>
      <Box
        sx={{
          flexGrow: 1,
          display: 'flex',
          flexDirection: 'column',
          gap: 0.25,
          overflowY: 'auto',
          width: '100%',
          alignItems: 'center',
          // pt กันวงกลม badge ของโมดูลแรกสุด (โปรไฟล์) โดนตัดครึ่งบน — เพราะ overflowY:'auto' ทำให้
          // browser คำนวณ overflowX เป็น 'auto' ไปด้วยอัตโนมัติ (ตาม CSS overflow spec: ถ้าตั้งแกนใดแกนหนึ่ง
          // เป็นค่าที่ไม่ใช่ visible อีกแกนจะไม่ใช่ visible ตามไปด้วย) แล้ว badge ที่ MUI translate ออกมา
          // ครึ่งหนึ่งของตัวเอง (เพื่อเกาะมุมขวาบนของไอคอน) ของ item แรกสุดในลิสต์ไม่มีที่ให้ poke ขึ้นไปเลย
          // โดนขอบบนของ scroll container ตัดวงกลมครึ่งบนทิ้งไปพอดี
          pt: 1,
          '::-webkit-scrollbar': { display: 'none' },
        }}
      >
        {moduleGroups.map((mod) => {
          const ModuleIcon = mod.moduleIcon;
          const humenPendingTotal = pendingLeaveCount + pendingOtCount + pendingFieldAllowanceCount + pendingTimestampApprovalCount;
          const moduleBadgeCount =
            mod.name === 'HUMEN' ? humenPendingTotal
            // อนุมัติการจ่ายเงิน/ตรวจสอบรายการเบิกเงิน ย้ายมาอยู่ใต้เมนู FINANCE แล้ว (ดู
            // sidebarNavConfig.js — group: 'ส่วนตัว' ใน sub ของแถว FINANCE) badge นับรวมของ
            // module icon แถบซ้ายสุดต้องตามไปด้วย ไม่งั้นเลขค้างอยู่ที่ไอคอน PROFILE ทั้งที่เมนูไม่ได้อยู่ที่นั่นแล้ว
            : mod.name === 'PROJECT' ? pendingBudgetCount
            : mod.name === 'FINANCE' ? pendingExpensesCount + pendingPaymentApprovalCount + pendingExpenseReviewCount + recurringDueCount
            : 0;
          const showBadge = moduleBadgeCount > 0;
          const isActive = activeModuleName === mod.name;
          return (
            <Tooltip key={mod.name} title={mod.name} placement="right">
            <Box
              onClick={() => {
                setActiveModuleName(mod.name);
                // ไม่ navigate จาก icon — ให้คลิก sub-item บน secondary rail เสมอ
              }}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 0.25,
                cursor: 'pointer',
                width: '100%',
                px: 1,
                py: 0.5,
                borderRadius: '14px',
                color: isActive ? '#fff' : 'rgba(255,255,255,0.45)',
                backgroundColor: isActive ? BRAND_ACTIVE : 'transparent',
                boxShadow: isActive ? BRAND_ACTIVE_SHADOW : 'none',
                transition: 'all 0.2s ease',
                '&:hover': {
                  backgroundColor: isActive ? BRAND_ACTIVE : 'rgba(255,255,255,0.1)',
                  color: '#fff',
                  transform: 'translateY(-2px)',
                },
              }}
            >
              {/* Badge ห่อแค่กล่องไอคอน (ไม่รวม label ข้อความ) — เดิมห่อทั้งคู่ทำให้ bounding box ที่ใช้
              คำนวณตำแหน่ง badge กลายเป็นสูงเท่าไอคอน+label รวมกัน แต่ anchor top-right ยังอิงจากขอบบนของ
              กล่องไอคอนเป๊ะ พอ badge ถูก translate ออกมาครึ่งหนึ่งของตัวเอง (ปกติของ MUI Badge) เลยไปเกย
              ขอบมุมบนขวาของกล่องพอดี (กล่องมี borderRadius 16px แต่ badge คำนวณจากมุมเหลี่ยมเดิม ไม่รู้จัก
              radius) ดูเหมือนลอยเกยขอบ/ไม่เนียน — ห่อแค่ Box 44x44 ให้ตรงไปตรงมา ไม่มี label มากวนการคำนวณ */}
              <Badge
                badgeContent={showBadge ? moduleBadgeCount : 0}
                invisible={!showBadge}
                color="error"
                sx={{ cursor: 'pointer' }}
              >
                <Box
                  sx={{
                    p: 1.5,
                    borderRadius: '16px',
                    backgroundColor:
                      activeModuleName === mod.name
                        ? BRAND_ACTIVE
                        : 'transparent',
                    boxShadow:
                      activeModuleName === mod.name
                        ? BRAND_ACTIVE_SHADOW
                        : 'none',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    width: 44,
                    height: 44,
                    mb: 0.25,
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: 'rgba(255, 255, 255, 0.12)',
                    },
                  }}
                >
                  <ModuleIcon sx={{ fontSize: 24 }} />
                </Box>
              </Badge>
              <Typography variant="caption" sx={{ fontSize: 10, fontWeight: 500, lineHeight: 1 }}>
                {SIDEBAR_MODULE_SHORT_TITLE[mod.name] || mod.name}
              </Typography>
              </Box>
            </Tooltip>
          );
        })}
      </Box>
      <Divider
        sx={{ width: '40%', my: 1, borderColor: 'rgba(255,255,255,0.1)' }}
      />
      <Tooltip title="ประกาศ" placement="right">
        <Box
          onClick={openAnnouncements}
          sx={{
            p: 0.75,
            mb: 0.5,
            cursor: 'pointer',
            color: announcementUnread > 0 ? '#fff' : 'rgba(255, 255, 255, 0.5)',
            transition: 'color 0.2s',
            '&:hover': { color: '#fff' },
          }}
        >
          <Badge badgeContent={announcementUnread} color="error">
            <AnnouncementOutlined sx={{ fontSize: 26 }} />
          </Badge>
        </Box>
      </Tooltip>
      <Tooltip title="Sign Out" placement="right">
        <Box
          onClick={onSignOut}
          sx={{
            p: 0.75,
            cursor: 'pointer',
            color: 'rgba(255, 255, 255, 0.5)',
            transition: 'color 0.2s',
            '&:hover': { color: '#ef4444' },
          }}
        >
          <i
            className="fas fa-sign-out-alt"
            style={{ fontSize: '1.4rem' }}
          ></i>
        </Box>
      </Tooltip>
    </Box>
  );

  /* Secondary rail — ข้อความอย่างเดียว ไม่มีไอคอนรายการ, ซ่อนโดย default โผล่มาเมื่อ hover แถบ 1 */
  const secondaryRailBox = (
    <Box
      sx={{
        width: SECONDARY_RAIL_WIDTH,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: '#fff',
        borderRight: '1px solid rgba(0, 0, 0, 0.05)',
      }}
    >
      <Box sx={{ p: 3, pb: 2 }}>
        {(orgBrand.logoUrl || orgBrand.company.name) && (
          <Box sx={{ mb: 1.5 }}>
            <OrgBrand
              logoHeight={32}
              nameSx={{ fontSize: '0.8rem', fontWeight: 700, color: 'text.primary', lineHeight: 1.2 }}
            />
          </Box>
        )}
        <Typography
          variant="h6"
          color="primary.dark"
          sx={{ fontWeight: 700, letterSpacing: 0.5 }}
        >
          {activeGroup?.title || 'Menu'}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {departmentName}
        </Typography>
      </Box>
      <Divider sx={{ mb: 1, mx: 3 }} />
      <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2, pb: 2 }}>
        {(() => {
          // isPaymentApprover/isExpenseReviewer เช็คซ้ำในทั้ง 2 branch (item ตรงๆ กับ item.sub) เพราะ
          // 2 เมนูนี้ย้ายมาอยู่ใน sub ของแถว FINANCE แล้ว (เดิมเป็นแถวเดี่ยวๆ ใต้ PROFILE) — ยังต้องซ่อน
          // จากคนที่ไม่ได้ถูกตั้งเป็นผู้อนุมัติ/ผู้ตรวจสอบเหมือนเดิมทุกประการ ไม่ได้เปลี่ยน logic การซ่อน
          const visibleItems = (activeGroup?.items || []).reduce((acc, item) => {
            if (item.sub && item.sub.length > 0) {
              item.sub.forEach((subItem) => {
                if (
                  accessRightSubMenu(
                    me,
                    item.name,
                    subItem.href,
                    subItem.level ?? 0,
                  ) &&
                  !hiddenMenus.has(subItem.href) &&
                  !(subItem.href === '/profile/budget-approval' && !isBudgetApprover(me, info)) &&
                  !(subItem.href === '/profile/payment-approval' && !isPaymentApprover(me, info)) &&
                  !(subItem.href === '/profile/expense-review' && !isExpenseReviewer(me, info))
                ) {
                  acc.push({ ...subItem, moduleName: item.name });
                }
              });
            } else if (
              accessRightSubMenu(me, item.name, item.href, 0) &&
              !hiddenMenus.has(item.href) &&
              !(item.href === '/profile/leave' && me?.userData?.type === 'DAILY' && !me?.userData?.daily_leave_enabled) &&
              !(item.href === '/profile/field-allowance' && !me?.userData?.field_allowance?.enabled) &&
              !(item.href === '/profile/payment-approval' && !isPaymentApprover(me, info)) &&
              !(item.href === '/profile/expense-review' && !isExpenseReviewer(me, info))
            ) {
              acc.push({ ...item, moduleName: item.name });
            }
            return acc;
          }, []);

          const getBadge = (href) => (
            href === '/humen/leave-request'
              ? pendingLeaveCount
              : href === '/humen/ot-request'
              ? pendingOtCount
              : href === '/humen/field-allowance-request'
              ? pendingFieldAllowanceCount
              : href === '/humen/timestamp-approval'
              ? pendingTimestampApprovalCount
              : href === '/finance/expenses'
              ? pendingExpensesCount
              : href === '/finance/expenses/recurring'
              ? recurringDueCount
              : href === '/humen/employee'
              ? pendingEmployeeCount
              : href === '/profile/budget-approval'
              ? pendingBudgetCount
              : href === '/profile/payment-approval'
              ? pendingPaymentApprovalCount
              : href === '/profile/expense-review'
              ? pendingExpenseReviewCount
              : 0
          );

          const renderNavItem = (item, index) => (
            <div key={item.title + index}>
              <NavItem
                href={item.href}
                title={item.title}
                sub={item.sub}
                me={me}
                moduleName={item.moduleName}
                highlight={item.highlight}
                badge={getBadge(item.href)}
              />
            </div>
          );

          // แยกรายการที่มี group (เช่น 'ส่วนตัว') ออกจาก list เรียบๆ ด้านบน — render เป็นกล่องมีกรอบ
          // ต่างหาก ห่างลงมาด้านล่างให้เห็นชัดว่าเป็นคนละกลุ่ม ไม่ปนกับเมนูหลักของโมดูลนั้น ถ้า filter
          // สิทธิ์แล้วกลุ่มไหนไม่เหลือเมนูเลย (เช่นไม่ใช่ทั้งผู้อนุมัติจ่ายเงินและผู้ตรวจสอบ) ไม่ต้องโชว์กรอบเปล่า
          const ungroupedItems = visibleItems.filter((i) => !i.group);
          const groupedMap = visibleItems.reduce((map, i) => {
            if (!i.group) return map;
            if (!map[i.group]) map[i.group] = [];
            map[i.group].push(i);
            return map;
          }, {});

          return (
            <>
              <List disablePadding>
                {ungroupedItems.map((item, index) => renderNavItem(item, index))}
              </List>
              {Object.entries(groupedMap).map(([groupName, groupItems]) => (
                <Box
                  key={groupName}
                  sx={{
                    mt: 3,
                    p: 1,
                    border: '1.5px dashed',
                    borderColor: 'primary.light',
                    borderRadius: 2,
                    bgcolor: 'rgb(var(--color-primary-500) / 0.04)',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      display: 'block',
                      fontWeight: 700,
                      color: 'primary.main',
                      pl: 1,
                      mb: 0.5,
                    }}
                  >
                    {groupName}
                  </Typography>
                  <List disablePadding>
                    {groupItems.map((item, index) => renderNavItem(item, index))}
                  </List>
                </Box>
              ))}
            </>
          );
        })()}
      </Box>
    </Box>
  );

  const content = (
    <Box
      sx={{
        display: 'flex',
        height: '100%',
        backgroundColor: 'background.paper',
        overflow: 'hidden',
        boxShadow: '4px 0 24px rgba(0,0,0,0.05)',
      }}
    >
      {primaryRailBox}
      {secondaryRailBox}
    </Box>
  );

  return (
    <div>
      <Hidden lgUp>
        <Drawer
          anchor="left"
          onClose={onMobileClose}
          open={openMobile}
          variant="temporary"
          PaperProps={{
            sx: {
              width: 320,
              border: 'none',
            },
          }}
        >
          {content}
        </Drawer>
      </Hidden>
      <Hidden lgDown>
        {/* แถบ 1 — ไอคอนโมดูล ค้างไว้เสมอ, hover แล้วแถบ 2 โผล่ */}
        <Box
          onMouseEnter={handlePeekEnter}
          onMouseLeave={handlePeekLeave}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            height: '100vh',
            zIndex: 1200,
          }}
        >
          {primaryRailBox}
        </Box>
        {/* แถบ 2 — ซ่อนโดย default, โผล่มาเมื่อ hover แถบ 1 หรือตัวมันเอง แล้วหุบกลับเมื่อเมาส์ออก */}
        <Box
          onMouseEnter={handlePeekEnter}
          onMouseLeave={handlePeekLeave}
          sx={{
            position: 'fixed',
            top: 0,
            left: PRIMARY_RAIL_WIDTH,
            height: '100vh',
            zIndex: 1190,
            transform: peek ? 'translateX(0)' : 'translateX(-100%)',
            transition: 'transform 0.2s ease',
            boxShadow: peek ? '4px 0 24px rgba(0,0,0,0.25)' : 'none',
            pointerEvents: peek ? 'auto' : 'none',
          }}
        >
          {secondaryRailBox}
        </Box>
      </Hidden>
    </div>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  me: PropTypes.object,
};

DashboardSidebar.defaultProps = {
  onMobileClose: () => {},
  openMobile: false,
};

export default DashboardSidebar;
